import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "JavaScript's native map reduce and filter are wrong",
  "description": "",
  "published": "2011-06-29T00:00:00.000Z",
  "redirect_from": ["/blog/javascripts-native-map-reduce-and-filter-are-wrong/swizec/1873"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Last week I saw `}<a parentName="p" {...{
        "href": "https://twitter.com/#!/jhartikainen/status/84490101673046016"
      }}>{`a tweet by @jhartikainen`}</a>{` that made my heart skip a beat! He totally raped the definition of a one liner and made something that can be beautiful only to a giant `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`functional programming`}</a>{` nerd.`}</p>
    <p>{`The gist that he posted looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`//Is this abuse of array functions? :D
"20000001"
  .split("")
  .reverse()
  .join("")
  .match(/\\d{3}|\\d+$/g)
  .map(function (s) {
    return s.split("").reverse().join("");
  })
  .reverse()
  .join(" ");
//produces "20 000 001"
`}</code></pre>
    <p>{`Now this might just look like so much brain sputtering madness to you, but it's actually displaying something very awesome about one of my favourite programming languages -- native implementations of map, reduce and so on.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/06/5883352394_72b348a448_m.jpg",
        "alt": "IMG_2307",
        "title": "IMG_2307"
      }}></img></p>
    <p><em parentName="p">{`tl;dr --> JavaScript is starting to mix functional and objective programming in a bad bad way`}</em></p>
    <p>{`I'm not exactly certain when `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScript`}</a>{` got native implementations of these as I've been using libraries like `}<a parentName="p" {...{
        "href": "http://documentcloud.github.com/underscore/",
        "title": "Underscore.js"
      }}>{`underscore.js`}</a>{` for a while now to achieve this exact same thing. Actually wikipedia tells me this has been added in version 1.6 way back in November 2005. Six years ago and I didn't know a thing about it.`}</p>
    {
      /*more*/
    }
    <p>{`That's just sad. Why don't more tutorials online talk about these features? How are people supposed to learn how awesome JavaScript is when most of the community works only with a very small subset of the language?`}</p>
    <p>{`Some further searching tells me that the version of JavaScript most browsers currently support is 1.5 and since `}<a parentName="p" {...{
        "href": "http://www.ecma-international.org/publications/standards/Ecma-262.htm",
        "title": "ECMAScript"
      }}>{`ECMAScript`}</a>{` version 5, which is comparable to JavaScript 1.8, was only standardised in ... December 2009, the browsers are quickly trying to pick up slack and get up to speed with that ...`}</p>
    <p>{`This makes me sad, I almost don't feel like continuing this post ... luckily though, Node.js is pretty good with the latest ECMAScript support so there's still hope.`}</p>
    <h2 {...{
      "id": "whats-wrong"
    }}>{`What's wrong`}</h2>
    <p>{`Considering the horror show I just described, writing the rest of this post leaves a bad taste in my mouth, but I think the way JavaScript natively implements some features of functional programming is wrong.`}</p>
    <p>{`The way map, reduce, filter and others are implemented in JavaScript is that they are methods of the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Associative_array",
        "title": "Associative array"
      }}>{`Array`}</a>{` class. And you call them as such:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[0, 1, 2].map(function (i) {
  return i + 1;
}); // adds 1 to every member

[0, 1, 2].reduce(function (a, b) {
  return a + b;
}); // sums members

[0, 1, 2].filter(function (i) {
  return i > 0;
}); // removes members smaller than 1
`}</code></pre>
    <p>{`As you can see all of these functions are a way to call a certain function on every member of the array. Nothing too special here, anyone who's ever even sniffed at functional programming should understand how these three functions work.`}</p>
    <p>{`But because JavaScript is a language that is both functional and objective in its design, this code suddenly becomes very ambiguous to read.`}</p>
    <p>{`On one hand, you're calling functions that are the epitome of functional programming. Obviously there are no side-effects and a copy of the array is returned, leaving the original intact. Immutable data structures and all that functional shebang.`}</p>
    <p>{`But on the other hand, you're calling these `}<em parentName="p">{`on`}</em>{` an array, as the array's methods. Obviously these functions belong to the Array and are therefore executed in place and the array's state is changed.`}</p>
    <p>{`Reading the syntax and knowing the basics of both objective and functional programming, both those opinions look like they should be true. Shouldn't they?`}</p>
    <p>{`Turns out, in this case, JavaScript returns a copy of the array and nothing at all happens to the original array. As we can demonstrate with the following quick test in the `}<a parentName="p" {...{
        "href": "http://nodejs.org/",
        "title": "Node.js"
      }}>{`node.js`}</a>{` console:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Merrick:~ Swizec$ node
> var a = [1,2,3];
> var b = a.map(function (i) { return i+1; });
> a
[ 1, 2, 3 ]
> b
[ 2, 3, 4 ]
> a.map(function (i) { return i+1; });
[ 2, 3, 4 ]
> a
[ 1, 2, 3 ]
`}</code></pre>
    <p>{`So it would seem that what we have here is a case of functional programming, behaving as functional programming, neatly packaged into an objective syntax.`}</p>
    <p>{`Which I guess wouldn't actually be that bad were it consistent.`}</p>
    <p>{`Other functions used in this exact same way usually change the array's state. Both sort and reverse return the new array ... but also change the original array's ordering. Similarly every other function on the array ranging from `}<em parentName="p">{`shift`}</em>{` and `}<em parentName="p">{`pop`}</em>{` to `}{`_`}{`splice `}{`_`}{`change the state of the array they are being called on and return ... something (it depends on the particular function).`}</p>
    <p>{`This means that reading JavaScript you are faced with the fact that functions called on objects are sometimes performing actions on the `}<em parentName="p">{`copy`}</em>{` of the object and sometimes on `}<em parentName="p">{`the object`}</em>{`.`}</p>
    <p>{`That's just confusing and bloody horrible from a language design standpoint don't you think?`}</p>
    <p>{`Why couldn't they just create new global functions for these awesome functional programming concepts ... I mean, the whole idea there is that functions get parameters and only parameters, functions in functional languages are supposed to be context independent. But here you have context dependent functional functions ... it's just a mess.`}</p>
    <p>{`Is JavaScript fast becoming like `}<a parentName="p" {...{
        "href": "http://www.php.net/",
        "title": "PHP"
      }}>{`PHP`}</a>{` with its confused standard library? Or is it just going through a little identity crysis?`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://peter.michaux.ca/articles/javascript-is-dead-long-live-javascript"
        }}>{`JavaScript is Dead. Long Live JavaScript`}</a>{` (peter.michaux.ca)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://greatfinds.icrossing.com/the-rise-of-javascript-micro-frameworks/"
        }}>{`The rise of JavaScript micro-frameworks`}</a>{` (greatfinds.icrossing.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.anscamobile.com/2011/06/lua-is-more-popular-than-javascript/"
        }}>{`Lua is more popular than JavaScript`}</a>{` (anscamobile.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.devcurry.com/2011/06/javascript-hash-table.html"
        }}>{`Hash Tables in JavaScript`}</a>{` (devcurry.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.duruk.net/2011/06/19/nodelists-and-arrays-in-javascript/"
        }}>{`NodeLists and Arrays in JavaScript`}</a>{` (duruk.net)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=638fd36f-9891-4a4d-8610-374131a43a05",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      